import React, {useState} from 'react';
import Button from '../../../../components/button';
import {EndpointConfigurationModal} from './endpoint-configuration-modal';
import Icon from 'shared/components/icon';
import styles from './endpoint-styles.module.scss';
import classnames from 'classnames';
import {IconButton} from '@mui/material';
import EndpointConfigurationService, {
  EndpointConfiguration,
} from '../../../../services/EndpointConfigurationService';
import {DeleteConfirmationModal} from './delete-confirmation-modal';
import {errorMessage, flashMessage} from '../../../../../shared/actions/notifications';
import {useDispatch} from 'react-redux';
import {keyBy, omit} from 'lodash';
import useAsyncEffect from '../../../../hooks/use-async-effect';
import {useBoolean} from '../../../../hooks/useBoolean';
import Loading from '../../../../../shared/components/loading';

interface Props {
  universityId: string;
}

const newEndpoint: EndpointConfiguration = {
  id: '',
  url: '',
  authenticationMethod: 'Basic Authentication',
  username: '',
  password: '',
};

const EndpointConfigurations = ({universityId}: Props) => {
  const [editEndpointConfigurationId, setEditEndpointConfigurationId] = useState<string | null>(
    null
  );
  const [endpoints, setEndpoints] = useState<Record<string, EndpointConfiguration>>({});
  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const dispatch = useDispatch();
  const loading = useBoolean(true);

  const deleteEndpoint = async (id: string): Promise<void> => {
    try {
      await EndpointConfigurationService.destroy(id, {universityId: universityId});
      setEndpoints(omit(endpoints, id));
      dispatch(flashMessage('You have successfully deleted the endpoint'));
    } catch (error) {
      dispatch(errorMessage('Something went wrong deleting your endpoint configuration'));
      console.error('error', error);
    }
  };

  useAsyncEffect(async () => {
    const fetchedEndpoints = await EndpointConfigurationService.index({
      universityId: universityId,
    });
    setEndpoints(keyBy(fetchedEndpoints, 'id'));
    loading.setFalse();
  }, [universityId]);

  return (
    <div className={styles.editCollegeProfile}>
      {idToDelete && (
        <DeleteConfirmationModal
          deleteEndpoint={() => deleteEndpoint(idToDelete)}
          url={endpoints[idToDelete]?.url || ''}
          onClose={(): void => {
            setIdToDelete(null);
          }}
        />
      )}
      {editEndpointConfigurationId && (
        <EndpointConfigurationModal
          initialValues={endpoints[editEndpointConfigurationId] || newEndpoint}
          updateEndpointConfigurations={(endpointConfiguration: EndpointConfiguration) =>
            setEndpoints({...endpoints, [endpointConfiguration.id]: endpointConfiguration})
          }
          universityId={universityId}
          onClose={() => setEditEndpointConfigurationId(null)}
        />
      )}
      <div className={styles.header}>
        <p className="shared-font title">Endpoints Configurations</p>
        <Button
          onClick={() => setEditEndpointConfigurationId('new')}
          outline
          className={styles.floatRight}
          size="tiny"
          margin={false}
        >
          + Add New Endpoint
        </Button>
      </div>
      <div
        className={classnames('mt-20', styles.endpointLineItem)}
        style={{flexDirection: 'column'}}
      >
        {loading.value && <Loading />}
        {Object.values(endpoints).map((endpoint) => (
          <div key={endpoint.url} className={classnames(styles.flexDiv)}>
            <div className={styles.endpointUrlContainer}>
              <p className="shared-font body m-0">{endpoint.url}</p>
            </div>
            <div className={styles.iconPosition}>
              <IconButton
                size="medium"
                className={styles.iconBlue}
                onClick={(): void => setEditEndpointConfigurationId(endpoint.id)}
              >
                <Icon iconType="pen" />
              </IconButton>
              <IconButton
                size="medium"
                className={styles.iconRed}
                onClick={(): void => {
                  if (endpoint.id) {
                    setIdToDelete(endpoint.id);
                  }
                }}
              >
                <Icon iconType="trash" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EndpointConfigurations;
